import catalogEn from './exercises/catalog_en.json';
import catalogUk from './exercises/catalog_uk.json';

const exerciseMapEn = new Map(catalogEn.map(e => [e.id, e]));
const exerciseMapUk = new Map(catalogUk.map(e => [e.id, e]));

function findExercise(exerciseId, isUk) {
    if (isUk) {
        return exerciseMapUk.get(exerciseId) || exerciseMapEn.get(exerciseId);
    }
    return exerciseMapEn.get(exerciseId) || exerciseMapUk.get(exerciseId);
}

const MUSCLE_NAMES = {
    en: {
        chest: "Chest",
        back: "Back",
        upper_back: "Upper Back",
        lower_back: "Lower Back",
        trapezius: "Trapezius",
        deltoids: "Shoulders",
        rear_deltoids: "Rear Delts",
        biceps: "Biceps",
        triceps: "Triceps",
        forearms: "Forearms",
        abs: "Abs",
        obliques: "Obliques",
        legs: "Legs",
        quadriceps: "Quadriceps",
        hamstrings: "Hamstrings",
        glutes: "Glutes",
        calves: "Calves",
        hip_flexors: "Hip Flexors"
    },
    uk: {
        chest: "Грудні м'язи",
        back: "М'язи спини",
        upper_back: "Верх спини",
        lower_back: "Поперек",
        trapezius: "Трапеції",
        deltoids: "Дельтоподібні м'язи (плечі)",
        rear_deltoids: "Задні дельти",
        biceps: "Біцепс",
        triceps: "Трицепс",
        forearms: "Передпліччя",
        abs: "Прес",
        obliques: "Косі м'язи живота",
        legs: "М'язи ніг",
        quadriceps: "Квадрицепси",
        hamstrings: "Біцепс стегна (задня поверхня)",
        glutes: "Сідничні м'язи",
        calves: "Литки",
        hip_flexors: "Згиначі стегна"
    }
};

const EQUIPMENT_NAMES = {
    en: {
        Barbell: "Barbell",
        Dumbbell: "Dumbbells",
        Dumbbells: "Dumbbells",
        Cable: "Cable Machine",
        Machine: "Machine",
        Bodyweight: "Bodyweight",
        'Smith Machine': "Smith Machine",
        Kettlebell: "Kettlebell",
        'Cardio Machine': "Cardio Machine",
        'Medicine Ball': "Medicine Ball",
        Functional: "Functional Equipment",
        'Specialty Bar': "Specialty Bar",
        Other: "Standard Gym Equipment"
    },
    uk: {
        Barbell: "Штанга",
        Dumbbell: "Гантелі",
        Dumbbells: "Гантелі",
        Cable: "Блоковий тренажер (кросовер)",
        Machine: "Тренажер",
        Bodyweight: "Власна вага",
        'Smith Machine': "Тренажер Сміта",
        Kettlebell: "Гиря",
        'Cardio Machine': "Кардіотренажер",
        'Medicine Ball': "Медбол",
        Functional: "Функціональне спорядження",
        'Specialty Bar': "Спеціальний гриф",
        Other: "Спортивний інвентар"
    }
};

// Handcrafted, deeply detailed guides for staple gym movements
const CURATED_GUIDES = {
    squat: {
        en: {
            instructions: [
                "Position the barbell in a squat rack at mid-chest height. Step under the bar and rest it across your upper back/trapezius. Grip the bar firmly, retract your shoulder blades, and unrack the weight with a solid base.",
                "Take two measured steps back. Set your feet shoulder-width apart (or slightly wider), with your toes pointed outwards at approximately 15–30 degrees.",
                "Take a deep diaphragmatic breath into your belly and brace your core 360 degrees as if preparing for impact.",
                "Initiate the movement by hinging at the hips and bending your knees simultaneously. Keep your chest proud and drive your knees outward in line with your toes.",
                "Descend under control until your hip crease drops below the top of your knees (parallel or below), keeping your weight balanced over your midfoot.",
                "Drive aggressively upward through the midfoot and heels, maintaining a neutral spine until you return to the starting position. Exhale past the sticking point."
            ],
            commonMistakes: [
                "Knee Cave (Valgus Collapse): Allowing your knees to collapse inward during the ascent. Actively push knees outward in line with your toes.",
                "Lumbar Rounding (Butt Wink): Rounding your lower back at the bottom of the squat. Only squat to the depth where you can maintain a neutral spine.",
                "Heel Lift: Shifting weight forward onto your toes. Keep your entire foot planted with even tripod pressure (big toe, pinky toe, heel).",
                "Bouncing out of the hole: Dropping uncontrollably into the bottom position. Control the descent for 2–3 seconds."
            ],
            tips: "Think of spreading the floor apart with your feet. This activates the glutes and locks the hips into a stable groove.",
            volume: "Strength: 3–5 sets of 4–6 reps (2–3 min rest). Hypertrophy: 3–4 sets of 8–12 reps (90–120s rest)."
        },
        uk: {
            instructions: [
                "Встановіть гриф на стійках на рівні середини грудей. Підійдіть під штангу, покладіть її на верхню частину трапецій або задні дельти. Щільно візьміться за гриф, зведіть лопатки та зніміть вагу зі стійок.",
                "Зробіть два короткі кроки назад. Поставте стопи на ширині плечей або трохи ширше, носки розведіть під кутом 15–30 градусів.",
                "Зробіть глибокий вдих у живіт і зафіксуйте м'язи кору на 360 градусів (внутрішньочеревний тиск).",
                "Почніть рух, одночасно згинаючи коліна та відводячи таз назад. Тримайте грудну клітку піднятою, спрямовуючи коліна чітко за напрямком носків.",
                "Опускайтеся під контролем до паралелі стегон із підлогою або трохи нижче, зберігаючи баланс на середині стопи.",
                "Потужно відштовхніться серединою стопи та п'ятами, розгинаючи коліна й таз. Зберігайте нейтральне положення спини до повного випрямлення та видихніть."
            ],
            commonMistakes: [
                "Зведення колін всередину (вальгус): стежте, щоб коліна рухалися чітко за лінією носків протягом усього підйому.",
                "Округлення попереку («клювок тазом»): опускайтеся лише до тієї глибини, де можете утримати поперек у нейтральному анатомічному положенні.",
                "Відрив п'ят від підлоги: переносьте вагу на середину стопи та п'яти, уникайте завалювання на носки.",
                "Використання відскоку внизу: не кидайте вагу різко вниз, тримайте постійне напруження м'язів стегон."
            ],
            tips: "Уявіть, що ви намагаєтеся розірвати підлогу стопами врізнобіч. Це вмикає середній сідничний м'яз і стабілізує кульшові суглоби.",
            volume: "Для сили: 3–5 підходів по 4–6 повторень (відпочинок 2–3 хв). Для гіпертрофії: 3–4 підходи по 8–12 повторень (відпочинок 90–120 с)."
        }
    },
    bench_press: {
        en: {
            instructions: [
                "Lie flat on the bench with your eyes directly beneath the racked barbell. Plant your feet firmly on the floor.",
                "Grip the bar slightly wider than shoulder-width. Squeeze your shoulder blades together and press them down into the bench to create a stable arch.",
                "Unrack the bar and bring it directly over your mid-chest with your elbows locked.",
                "Lower the bar smoothly to your lower-to-mid sternum, tucking your elbows at approximately 45–75 degrees from your torso. Inhale during the descent.",
                "Touch the chest lightly without bouncing, pause for a fraction of a second, then press the bar explosively upward and slightly back toward your eye-line.",
                "Lock out your arms at the top without losing shoulder blade retraction, and exhale."
            ],
            commonMistakes: [
                "Elbow Flaring at 90 Degrees: Paring elbows out perpendicular to the torso places excessive strain on the shoulder rotator cuffs. Tuck elbows to ~45–70°.",
                "Bouncing the bar off the ribcage: Using rib elasticity rather than muscular contraction risks injury and limits chest activation.",
                "Lifting the hips off the bench: Keep your glutes firmly planted on the bench throughout the entire set to comply with proper form.",
                "Losing upper back tightness: Shrugging shoulders up at lockout destroys your base. Keep scapulae retracted and depressed."
            ],
            tips: "Drive your legs into the floor (leg drive) to stabilize your torso and transfer power through the bench.",
            volume: "Strength: 4–5 sets of 3–5 reps. Muscle Growth: 3–4 sets of 8–10 reps."
        },
        uk: {
            instructions: [
                "Ляжте на лаву так, щоб очі були під грифом. Щільно притисніть стопи до підлоги на ширині плечей.",
                "Візьміться за гриф закритим хватом трохи ширше за плечі. Зведіть лопатки разом і опустіть їх донизу, утворивши стабільний грудний міст.",
                "Зніміть штангу зі стійок і зафіксуйте її на прямих руках над верхньою лінією грудей.",
                "Контрольовано опустіть штангу на середину або нижню частину грудей, тримаючи лікті під кутом 45–70 градусів до корпусу. Робіть вдих під час опускання.",
                "Торкніться грудей без відскоку, зафіксуйте на частку секунди і потужним рухом вичавіть штангу вгору та злегка назад (траєкторія за дугою).",
                "Зафіксуйте положення у верхній точці, не втрачаючи зведених лопаток, і зробіть видих."
            ],
            commonMistakes: [
                "Розведення ліктів під 90 градусів: створює надмірне травматичне навантаження на плечові суглоби. Тримайте лікті під кутом 45–70°.",
                "Відбивання штанги від грудної клітки: відскок забирає навантаження з м'язів і небезпечний для грудини.",
                "Відривання сідниць від лави: сідниці мають залишатися щільно притиснутими до поверхні лави.",
                "Втрата напруження в лопатках: розведення лопаток у верхній точці зміщує навантаження з грудей на передні дельти."
            ],
            tips: "Використовуйте упор ногами в підлогу («leg drive»), щоб передати стабільність через усе тіло.",
            volume: "Для сили: 4–5 підходів по 3–5 повторень. Для росту м'язів: 3–4 підходи по 8–10 повторень."
        }
    },
    deadlift: {
        en: {
            instructions: [
                "Stand with your midfoot directly beneath the barbell, feet hip-width apart. The bar should be about one inch from your shins.",
                "Hinge at your hips and grip the barbell just outside your knees with an overhand or mixed grip.",
                "Bend your knees until your shins lightly graze the bar. Do not let the barbell move from its position over the midfoot.",
                "Lift your chest, pull the slack out of the barbell until you hear the click, and engage your lats by squeezing your armpits.",
                "Take a deep breath into your core, brace hard, and push the floor away with your legs.",
                "Keep the bar dragging close along your shins and thighs. Lock out with glutes and hips tall without hyperextending your spine. Reverse the motion smoothly to return the bar to the ground."
            ],
            commonMistakes: [
                "Rounding the lumbar spine: Compromises disc health. Keep spine in a neutral alignment.",
                "Bar drifting away from the body: Greatly increases the moment arm on your lower back. Keep the bar in contact with your legs.",
                "Hyperextending at the top: Leaning back past vertical puts shear stress on spinal discs. Lock out straight tall.",
                "Jerking the weight from the floor: Pull slack first before exploding upward."
            ],
            tips: "Think of pushing the earth away with your legs rather than pulling the bar up with your arms.",
            volume: "Strength & Power: 3–5 sets of 3–5 reps with full 3-minute rest periods."
        },
        uk: {
            instructions: [
                "Станьте перед штангою так, щоб гриф проходив точно над серединою стопи (1–2 см від гомілок). Стопи на ширині таза.",
                "Зробіть нахил у тазостегнових суглобах і візьміться за гриф на ширині плечей прямим або різнохватом.",
                "Зігніть коліна, поки гомілки легко не торкнуться грифа. Штанга не повинна зрушуватися з місця.",
                "Підніміть груди, звівши найширші м'язи спини (уявіть, що стискаєте лимони пахвами), і виберіть люфт штанги.",
                "Зробіть потужний вдих у живіт, напружте прес і почніть рух із вдавлювання стоп у підлогу.",
                "Ведіть гриф уздовж гомілок і стегон, зберігаючи нейтральну спину. Зафіксуйте вертикальне положення стисканням сідниць і контрольовано опустіть вагу."
            ],
            commonMistakes: [
                "Округлення попереку («горб»): тримайте спину рівною від куприка до потилиці.",
                "Віддалення грифа від тіла: ведіть штангу впритул до ніг, інакше навантаження на поперек різко зростає.",
                "Перерозгинання назад у верхній точці: не прогинайтеся у попереку, фіксуйтеся вертикально за рахунок сідниць.",
                "Ривок зі старту: спочатку створіть попереднє натягнення («виберіть люфт»), потім починайте рух."
            ],
            tips: "Сприймайте тягу як рух відштовхування підлоги ногами від себе, а не підтягування ваги руками.",
            volume: "Для сили: 3–5 підходів по 3–5 повторень із повноцінним відпочинком до 3 хвилин."
        }
    },
    overhead_press: {
        en: {
            instructions: [
                "Set the barbell in a rack at collarbone height. Grip the bar just outside shoulder width with wrists stacked over forearms.",
                "Unrack the bar across your front deltoids. Squeeze your glutes, flex your quads, and brace your core tight.",
                "Tilt your head slightly back to clear the chin. Press the bar straight upward in a vertical path.",
                "Once the bar passes your forehead, push your head through the window created by your arms.",
                "Lock out the weight overhead directly over the crown of your head, shrugging your traps up slightly at the top.",
                "Lower under control back to your collarbone while keeping the elbows forward."
            ],
            commonMistakes: [
                "Excessive lower back arching: Avoid leaning back into an incline bench press. Squeeze glutes and abs to keep ribs down.",
                "Flaring elbows wide: Keep elbows angled slightly forward in front of the bar.",
                "Pressing around the head: Move your head back out of the way rather than looping the bar outward."
            ],
            tips: "Keep your entire body as rigid as a statue from your feet to your shoulders.",
            volume: "3–4 sets of 5–8 reps."
        },
        uk: {
            instructions: [
                "Встановіть гриф на рівні ключиць. Візьміться за штангу трохи ширше за плечі, кисті над ліктями.",
                "Зніміть штангу на передні дельти й верх грудей. Напружте сідниці, квадрицепси й прес для максимальної стійкості.",
                "Злегка відведіть підборіддя назад і вичавіть штангу вертикально вгору.",
                "Як тільки гриф пройде рівень чола, поверніть голову вперед у нейтральне положення.",
                "Зафіксуйте штангу над верхівкою голови на прямих руках, трохи піднявши трапеції у верхній точці.",
                "Плавно опустіть штангу назад на ключиці, тримаючи лікті направленими трохи вперед."
            ],
            commonMistakes: [
                "Надмірний прогин у попереку: тримайте прес і сідниці жорстко напруженими, щоб захистити хребет.",
                "Розведення ліктів у боки: лікті мають дивитися вперед під кутом близько 30–45°.",
                "Обведення голови по дузі: рухайте гриф строго вертикально, відводячи голову."
            ],
            tips: "Все тіло нижче плечей має бути твердим і непорушним, як колона.",
            volume: "3–4 підходи по 5–8 повторень."
        }
    },
    barbell_row: {
        en: {
            instructions: [
                "Stand with your feet shoulder-width apart, holding a barbell with an overhand grip slightly wider than your knees.",
                "Hinge forward at the hips with a slight bend in your knees until your torso is approximately 45 degrees or nearly parallel to the floor.",
                "Maintain a rigid neutral spine with your head aligned and lats pre-engaged.",
                "Pull the barbell toward your lower ribs / belly button, leading with your elbows and driving your shoulder blades together.",
                "Hold the contraction for one second at the top, feeling the back muscles squeeze hard.",
                "Lower the bar under strict control until your arms are fully extended without rounding your upper back."
            ],
            commonMistakes: [
                "Using body momentum: Jerking the torso upward to heave the weight up reduces back activation.",
                "Rounding the lower back: Keep chest out and core tight to safeguard your spine.",
                "Pulling too high toward the throat: Pull toward the waist/navel to target the lats rather than the neck."
            ],
            tips: "Imagine pulling through your elbows rather than gripping and pulling with your hands.",
            volume: "3–4 sets of 8–12 reps."
        },
        uk: {
            instructions: [
                "Станьте зі штангою, стопи на ширині плечей, хват трохи ширший за коліна.",
                "Зробіть нахил корпусу вперед за рахунок відведення таза назад, коліна злегка зігнуті. Кут нахилу корпусу 45° або нижче.",
                "Зберігайте пряму спину від попереку до голови, розправте плечі.",
                "Потягніть штангу до низу живота або талії, ведучи лікті вгору і назад та зводячи лопатки.",
                "Зафіксуйте скорочення м'язів спини на 1 секунду у верхній точці.",
                "Під повним контролем опустіть штангу у вихідне положення до повного випрямлення рук."
            ],
            commonMistakes: [
                "Використання інерції корпусу: підкидання ваги за рахунок розгинання спини знижує роботу найширших м'язів.",
                "Округлення спини: постійно контролюйте поперековий відділ і не горбтеся.",
                "Тяга до грудей замість живота: тягніть гриф до пупка, щоб навантажувати спину, а не трапецію та плечі."
            ],
            tips: "Уявіть, що ви тягнете ліктями, наче вони є гаками, вимикаючи передпліччя.",
            volume: "3–4 підходи по 8–12 повторень."
        }
    },
    pull_up: {
        en: {
            instructions: [
                "Grasp a pull-up bar with an overhand grip slightly wider than shoulder width.",
                "Hang with arms fully extended into a dead hang. Depress and retract your scapulae (pull shoulders down away from ears).",
                "Drive your elbows down toward your hips while pulling your chest up toward the bar.",
                "Continue pulling until your chin clears the bar or your collarbone reaches the bar.",
                "Pause briefly at the peak of the movement, then lower under strict control over 2–3 seconds back to a dead hang."
            ],
            commonMistakes: [
                "Kicking or swinging (kipping): Use pure muscular force without swinging legs.",
                "Half reps: Not descending to a full extension limits lat stretch and growth.",
                "Rounding shoulders forward at the top: Keep your chest lifted high."
            ],
            tips: "Think of driving the bar down toward your chest rather than pulling your body up.",
            volume: "3–4 sets to technical failure or 6–10 reps."
        },
        uk: {
            instructions: [
                "Візьміться за турнік прямим хватом трохи ширше за ширину плечей.",
                "Повністю повисніть на прямих руках. Опустіть лопатки донизу, відвівши плечі від вух (активний вис).",
                "Потягніть лікті вниз до талії, спрямовуючи груди вгору до поперечини.",
                "Продовжуйте рух, поки підборіддя не перетне лінію турніка, а груди не наблизяться до нього.",
                "Зафіксуйте пікове скорочення на мить і повільно опустіться за 2–3 секунди у вихідне положення."
            ],
            commonMistakes: [
                "Ривки ногами та розгойдування: виконуйте рух чисто за рахунок м'язів спини та рук.",
                "Неповна амплітуда: не опускаючись до кінця, ви втрачаєте фазу розтягнення найширших м'язів.",
                "Завалювання плечей уперед у верхній точці: груди мають прагнути до турніка."
            ],
            tips: "Думайте не про підтягування себе, а про опускання турніка вниз до грудей ліктями.",
            volume: "3–4 підходи по 6–10 повторень або до технічної відмови."
        }
    }
};

/**
 * Algorithmic Guide Generator for any exercise based on muscle, equipment, and movement.
 */
function generateDynamicGuide(exercise, lang) {
    const isUk = lang === 'uk';
    const name = exercise.name || exercise.id;
    const primaryMuscle = exercise.primaryMuscle || 'chest';
    const muscleName = (MUSCLE_NAMES[lang] && MUSCLE_NAMES[lang][primaryMuscle]) || primaryMuscle;
    const equipName = (EQUIPMENT_NAMES[lang] && EQUIPMENT_NAMES[lang][exercise.equipment]) || exercise.equipment || (isUk ? "обладнання" : "equipment");

    const idLower = (exercise.id || "").toLowerCase();
    const nameLower = (name || "").toLowerCase();

    // Categorize pattern
    let pattern = 'general';
    if (idLower.includes('press') || nameLower.includes('press') || idLower.includes('push_up') || idLower.includes('dip')) {
        pattern = 'press';
    } else if (idLower.includes('squat') || nameLower.includes('squat') || idLower.includes('lunge') || idLower.includes('step_up')) {
        pattern = 'squat';
    } else if (idLower.includes('deadlift') || idLower.includes('hinge') || idLower.includes('good_morning') || idLower.includes('thrust')) {
        pattern = 'hinge';
    } else if (idLower.includes('row') || idLower.includes('pull') || idLower.includes('lat') || idLower.includes('chin')) {
        pattern = 'pull';
    } else if (idLower.includes('curl') || nameLower.includes('curl')) {
        pattern = 'curl';
    } else if (idLower.includes('extension') || idLower.includes('pushdown') || idLower.includes('crusher')) {
        pattern = 'extension';
    } else if (idLower.includes('raise') || idLower.includes('fly') || idLower.includes('lateral')) {
        pattern = 'raise';
    } else if (idLower.includes('crunch') || idLower.includes('plank') || idLower.includes('situp') || idLower.includes('twist') || idLower.includes('rollout')) {
        pattern = 'core';
    } else if (idLower.includes('calf') || idLower.includes('calves')) {
        pattern = 'calves';
    }

    if (isUk) {
        let instructions = [];
        let mistakes = [];
        let tips = "";
        let volume = "3–4 підходи по 8–12 повторень із контрольованим темпом.";

        switch (pattern) {
            case 'press':
                instructions = [
                    `Займіть стабільне вихідне положення, використовуючи ${equipName}. Зафіксуйте корпус, зведіть лопатки та підтримуйте природний прогин у спині.`,
                    `Візьміться надійним хватом, розташувавши кисті строго на одній лінії з передпліччями для захисту суглобів.`,
                    `Зробіть глибокий вдих і плавно опустіть вагу під контролем, зберігаючи постійне напруження в ${muscleName}.`,
                    `У нижній точці відчуйте розтягнення цільових м'язів без різких відскоків.`,
                    `Потужним рухом вичавіть вагу у вихідне положення, роблячи видих на зусиллі, не блокуючи суглоби намертво.`
                ];
                mistakes = [
                    "Використання інерції або відскоку замість ізольованої роботи м'язів.",
                    "Неправильне положення ліктів, що створює зайве навантаження на плечові суглоби.",
                    "Втрата напруження в корі та надмірне вигинання спини."
                ];
                tips = `Зосередьтеся на відчутті роботи ${muscleName}. Контролюйте фазу опускання не менше 2 секунд.`;
                volume = "3–4 підходи по 6–10 повторень (відпочинок 90–120 с).";
                break;

            case 'pull':
                instructions = [
                    `Налаштуйте ${equipName} та займіть надійну позицію з прямим хребтом та розправленими плечима.`,
                    `Візьміться за рукоятку або гриф, зробіть передпопереднє натягнення та розправте лопатки.`,
                    `Почніть тягу з відведення ліктів назад, концентруючись на скороченні ${muscleName}.`,
                    `У точці пікового скорочення зведіть лопатки та затримайте рух на 1 секунду.`,
                    `Повільно поверніться у вихідне положення, відчуваючи плавне розтягнення спини.`
                ];
                mistakes = [
                    "Тяга руками замість активації найширших м'язів та зведення лопаток.",
                    "Розгойдування корпусом для підкидання ваги за рахунок інерції.",
                    "Округлення спини у фазі розтягнення."
                ];
                tips = "Тягніть ліктями назад, а не долонями, щоб максимально задіяти м'язи спини.";
                volume = "3–4 підходи по 8–12 повторень.";
                break;

            case 'squat':
                instructions = [
                    `Станьте стійко, утримуючи ${equipName}. Стопи поставте на ширині плечей, носки розведіть під природним кутом.`,
                    `Напружте м'язи пресу, зведіть лопатки та тримайте погляд спрямованим вперед.`,
                    `Почніть рух із згинання колін та відведення таза назад, спрямовуючи коліна за лінією носків.`,
                    `Опустіться до комфортної глибини (стегна паралельні підлозі), утримуючи вагу по центру стопи.`,
                    `Відштовхніться серединою стопи та підніміться у вихідне положення, видихаючи у верхній третині амплітуди.`
                ];
                mistakes = [
                    "Зведення колін всередину під час підйому.",
                    "Відрив п'ят від підлоги через зміщення центру ваги на носки.",
                    "Округлення поперекового відділу спини в нижній точці."
                ];
                tips = "Тримайте груди розправленими, а коліна розводьте за напрямком пальців ніг.";
                volume = "3–4 підходи по 8–12 повторень.";
                break;

            case 'hinge':
                instructions = [
                    `Займіть стійку зі стопами на ширині таза, утримуючи ${equipName}.`,
                    `Зафіксуйте пряме положення хребта, розправте плечі та трохи пом'якшіть коліна.`,
                    `Відводьте таз назад, нахиляючи корпус уперед за рахунок кульшових суглобів, зберігаючи поперек рівним.`,
                    `Опускайте вагу вздовж ніг, поки не відчуєте сильне натягнення в задній поверхні стегна та сідницях.`,
                    `Потужним рухом виштовхніть таз уперед за рахунок скорочення сідниць та поверніться у вертикальне положення.`
                ];
                mistakes = [
                    "Округлення попереку під час нахилу вперед.",
                    "Надмірне згинання колін (перетворення тяги на присідання).",
                    "Відведення ваги далеко від лінії ніг."
                ];
                tips = "Спрямовуйте рух тазом назад, немов намагаєтеся торкнутися стіни позаду себе.";
                volume = "3–4 підходи по 8–10 повторень.";
                break;

            case 'curl':
                instructions = [
                    `Встаньте або сядьте прямо, утримуючи ${equipName} у витягнутих руках вздовж корпусу.`,
                    `Притисніть лікті до боків і тримайте плечі опущеними та нерухомими.`,
                    `На видиху зігніть руки в ліктях, піднімаючи вагу виключно за рахунок скорочення ${muscleName}.`,
                    `У верхній точці зафіксуйте пікове скорочення біцепса на 1 секунду.`,
                    `Плавно та під контролем опустіть вагу за 2–3 секунди у вихідне положення до повного розгинання.`
                ];
                mistakes = [
                    "Розгойдування тулубом (читінг) для закидання ваги вгору.",
                    "Висування ліктів уперед, що знімає навантаження з біцепса.",
                    "Неповне опускання ваги вниз."
                ];
                tips = "Тримайте лікті строго нерухомими біля тулуба протягом усього підходу.";
                volume = "3–4 підходи по 10–12 повторень.";
                break;

            case 'extension':
                instructions = [
                    `Налаштуйте ${equipName} та зафіксуйте стабільне положення тіла з напруженим кором.`,
                    `Розташуйте лікті у фіксованому положенні, звідки відбуватиметься ізольований рух.`,
                    `Розігніть руки або ноги в суглобах, направляючи зусилля безпосередньо в ${muscleName}.`,
                    `У точці повного розгинання відчуйте максимальне скорочення м'яза.`,
                    `Контрольовано поверніться у вихідне положення, утримуючи темп без ривків.`
                ];
                mistakes = [
                    "Зміщення положення суглобів під час виконання руху.",
                    "Різке відпускання ваги у зворотній фазі.",
                    "Використання занадто важкої ваги, що порушує ізоляцію."
                ];
                tips = "Рухайтеся виключно в цільовому суглобі, всі інші частини тіла мають бути зафіксовані.";
                volume = "3–4 підходи по 10–15 повторень.";
                break;

            case 'raise':
                instructions = [
                    `Станьте прямо, тримаючи ${equipName}. Злегка нахиліть корпус уперед, напружте прес.`,
                    `Руки злегка зігнуті в ліктях, плечі опущені донизу.`,
                    `Підніміть руки через боки або вперед до рівня паралелі з підлогою, концентруючись на ${muscleName}.`,
                    `Затримайтеся на частку секунди у верхній точці амплітуди.`,
                    `Плавно опустіть вагу під контролем, не кидаючи її вниз.`
                ];
                mistakes = [
                    "Підкидання ваги ногами або спиною (читінг).",
                    "Підйом плечей до вух за рахунок трапецій.",
                    "Прямі заблоковані лікті під час руху."
                ];
                tips = "Уявіть, що ви виливаєте воду з глечиків у верхній точці, щоб навантажити бічні дельти.";
                volume = "3–4 підходи по 12–15 повторень.";
                break;

            case 'core':
                instructions = [
                    `Займіть вихідне положення на килимку або тренажері, використовуючи ${equipName}.`,
                    `Притисніть поперек або зафіксуйте нейтральну лінію хребта, напружте глибокі м'язи живота.`,
                    `Виконайте рух за рахунок скорочення м'язів пресу, наближаючи грудну клітку до таза.`,
                    `Зробіть повний видих у точці максимального стискання м'язів кору.`,
                    `Повільно поверніться у вихідну позицію, не розслабляючи прес повністю.`
                ];
                mistakes = [
                    "Тяга голови та шиї руками, що створює травматичне навантаження на хребці.",
                    "Прогин у попереку та відрив нижньої частини спини від опори.",
                    "Затримка дихання під час підходу."
                ];
                tips = "Зосередьтеся на скороченні відстані між нижніми ребрами та тазом, а не на амплітуді підйому.";
                volume = "3–4 підходи по 15–20 повторень або утримання 30–45 секунд.";
                break;

            case 'calves':
                instructions = [
                    `Займіть положення на платформі або тренажері, використовуючи ${equipName}. Подушечки стоп щільно притиснуті до краю.`,
                    `Опустіть п'яти нижче лінії платформи, відчувши повне розтягнення литкових м'язів.`,
                    `Потужно підніміться на носки якомога вище за рахунок скорочення ${muscleName}.`,
                    `Зафіксуйте пікове положення на 1–2 секунди у верхній точці.`,
                    `Повільно опустіться у вихідне положення з глибоким розтягненням.`
                ];
                mistakes = [
                    "Пружне відскакування на ахіллових сухожиллях без паузи.",
                    "Неповна амплітуда підйому на носки.",
                    "Згинання в колінах під час виконання вправи стоячи."
                ];
                tips = "Обов'язково робіть паузу в 1 секунду внизу для гасіння еластичної енергії сухожилля.";
                volume = "3–4 підходи по 12–15 повторень із повною амплітудою.";
                break;

            default:
                instructions = [
                    `Підготуйте ${equipName} та займіть стійке анатомічне положення.`,
                    `Напружте м'язи кору, зафіксуйте суглоби та спрямуйте увагу на цільову групу (${muscleName}).`,
                    `Виконайте фазу скорочення плавно за 1–2 секунди, утримуючи повний контроль над рухом.`,
                    `Зробіть видих у фазі максимального навантаження та зафіксуйте позицію на секунду.`,
                    `Опустіть або поверніть вагу у вихідне положення за 2–3 секунди, зберігаючи напруження.`
                ];
                mistakes = [
                    "Використання ривків та неконтрольованої інерції.",
                    "Порушення правильної постави та вигину хребта.",
                    "Неповна робоча амплітуда руху."
                ];
                tips = `Фокусуйтеся на зв'язку мозок-м'яз для якісного залучення ${muscleName}.`;
                volume = "3–4 підходи по 10–12 повторень.";
                break;
        }

        return { instructions, commonMistakes: mistakes, tips, volume };
    } else {
        // English dynamic guide
        let instructions = [];
        let mistakes = [];
        let tips = "";
        let volume = "3–4 sets of 8–12 reps with controlled tempo.";

        switch (pattern) {
            case 'press':
                instructions = [
                    `Set up in a stable position utilizing your ${equipName}. Keep your core braced, shoulder blades retracted, and maintain a natural spinal arch.`,
                    `Grip firmly with wrists stacked directly above your forearms to protect joint integrity.`,
                    `Inhale and lower the weight smoothly under strict control, keeping constant tension on the ${muscleName}.`,
                    `Feel a safe, full stretch across the target musculature without bouncing.`,
                    `Press the weight powerfully back to the starting position while exhaling past the sticking point. Do not harshly lock out joints.`
                ];
                mistakes = [
                    "Using momentum or bouncing instead of muscular tension.",
                    "Flaring elbows excessively wide, stressing the shoulder joints.",
                    "Losing core tightness and overarching the lower back."
                ];
                tips = `Focus on actively squeezing the ${muscleName} at the peak of each repetition.`;
                volume = "3–4 sets of 6–10 reps (90–120s rest).";
                break;

            case 'pull':
                instructions = [
                    `Position yourself firmly on your ${equipName} with your spine neutral and shoulders set down and back.`,
                    `Grip the handle or bar firmly, taking up all slack before initiating movement.`,
                    `Drive your elbows backward, focusing on contracting the ${muscleName}.`,
                    `Squeeze your shoulder blades together at the top of the movement and hold for a one-second pause.`,
                    `Lower the weight under complete control over 2–3 seconds back to the fully stretched starting position.`
                ];
                mistakes = [
                    "Pulling with the biceps rather than initiating with the lats and scapulae.",
                    "Rocking your torso excessively to heave the weight up.",
                    "Rounding your upper back during the eccentric stretch."
                ];
                tips = "Think of pulling through your elbows to maximize back recruitment.";
                volume = "3–4 sets of 8–12 reps.";
                break;

            case 'squat':
                instructions = [
                    `Stand with a stable stance using your ${equipName}, feet shoulder-width apart and toes angled slightly outward.`,
                    `Brace your core tightly, pull your shoulders back, and keep your gaze straight ahead.`,
                    `Hinge at the hips and bend your knees simultaneously, keeping knees tracking in line with your toes.`,
                    `Descend under control until your thighs are at least parallel to the floor, balancing weight across midfoot.`,
                    `Drive through the midfoot and heels back to the top position, exhaling as you complete the ascent.`
                ];
                mistakes = [
                    "Letting knees cave inward during the drive upward.",
                    "Lifting heels off the ground and shifting weight to the toes.",
                    "Rounding the lumbar spine at the bottom of the squat."
                ];
                tips = "Keep your chest high and push the floor away as you rise.";
                volume = "3–4 sets of 8–12 reps.";
                break;

            case 'hinge':
                instructions = [
                    `Stand with feet hip-width apart holding your ${equipName}.`,
                    `Keep your spine rigid, shoulders pulled back, and knees slightly soft.`,
                    `Push your hips back as your torso tilts forward, keeping the weight close to your legs.`,
                    `Descend until you feel a deep stretch in your hamstrings and glutes without rounding your spine.`,
                    `Drive your hips forward by squeezing your glutes to return to a tall standing posture.`
                ];
                mistakes = [
                    "Rounding the lower back during the forward hinge.",
                    "Bending knees excessively, turning the hinge into a squat.",
                    "Letting the weight drift away from the center of gravity."
                ];
                tips = "Imagine pushing your hips back to touch a wall behind you.";
                volume = "3–4 sets of 8–10 reps.";
                break;

            case 'curl':
                instructions = [
                    `Stand or sit tall holding your ${equipName} with arms fully extended.`,
                    `Pin your elbows close to your torso and keep your shoulders depressed and still.`,
                    `Curl the weight upward by flexing your elbows, focusing entirely on contracting the ${muscleName}.`,
                    `Squeeze hard at the peak contraction for a brief pause.`,
                    `Lower the weight under steady control over 2–3 seconds to a full arm extension.`
                ];
                mistakes = [
                    "Swinging your torso to fling the weights up.",
                    "Allowing elbows to drift forward, decreasing bicep tension.",
                    "Cutting the range of motion short at the bottom."
                ];
                tips = "Keep your upper arms locked in place like hinges.";
                volume = "3–4 sets of 10–12 reps.";
                break;

            case 'extension':
                instructions = [
                    `Secure your position on your ${equipName} with your core braced.`,
                    `Anchor your joints in a fixed position to isolate the movement.`,
                    `Extend your limbs smoothly, channeling force directly through the ${muscleName}.`,
                    `Hold the fully extended position for a deliberate contraction.`,
                    `Return to the start position with a slow, controlled negative phase.`
                ];
                mistakes = [
                    "Letting other body parts shift to assist the movement.",
                    "Allowing the weight to slam back down without resistance.",
                    "Using excessive weight that compromises isolation."
                ];
                tips = "Isolate movement purely to the active joint without shoulder or hip sway.";
                volume = "3–4 sets of 10–15 reps.";
                break;

            case 'raise':
                instructions = [
                    `Stand tall holding your ${equipName}. Maintain a slight forward torso lean and engaged core.`,
                    `Keep a soft bend in your elbows with shoulders pushed down away from your ears.`,
                    `Raise the weights outward or upward until parallel to the floor, leading with the ${muscleName}.`,
                    `Hold briefly at the top of the range of motion.`,
                    `Lower under control over 2 seconds without letting weights drop.`
                ];
                mistakes = [
                    "Using body momentum or leg drive to bounce weights up.",
                    "Shrugging your traps up toward your ears.",
                    "Locking elbows completely straight."
                ];
                tips = "Lead with the elbows and pour imaginary pitchers of water at the top.";
                volume = "3–4 sets of 12–15 reps.";
                break;

            case 'core':
                instructions = [
                    `Position yourself on the mat or apparatus with your ${equipName}.`,
                    `Press your lower back flat and brace your deep abdominal wall.`,
                    `Initiate the movement by flexing your spine or hips using pure core contraction.`,
                    `Exhale completely at peak contraction, bringing ribs toward your pelvis.`,
                    `Return slowly to the starting position without releasing abdominal tension.`
                ];
                mistakes = [
                    "Pulling on your neck or head with your hands.",
                    "Arching the lower back away from the floor or pad.",
                    "Holding your breath throughout the set."
                ];
                tips = "Focus on bringing your lower ribs closer to your pelvis.";
                volume = "3–4 sets of 15–20 reps or 30–45s isometric hold.";
                break;

            case 'calves':
                instructions = [
                    `Position the balls of your feet on the edge of the platform with your ${equipName}.`,
                    `Lower your heels below the edge until you feel a deep stretch in the calves.`,
                    `Press through the balls of your feet to raise up as high as possible onto your toes.`,
                    `Hold the peak contraction for a solid 1–2 seconds.`,
                    `Lower down slowly, pausing for a moment at the bottom to eliminate elastic bounce.`
                ];
                mistakes = [
                    "Bouncing rapidly on your Achilles tendon.",
                    "Cutting the top contraction short.",
                    "Bending knees during standing calf raises."
                ];
                tips = "Pause for a full second at the bottom to remove elastic recoil.";
                volume = "3–4 sets of 12–15 reps with full range.";
                break;

            default:
                instructions = [
                    `Set up with your ${equipName} in a balanced, athletic stance.`,
                    `Engage your core, stabilize your posture, and focus attention on the ${muscleName}.`,
                    `Execute the contraction phase smoothly over 1–2 seconds with full control.`,
                    `Exhale during peak effort and hold the position for a moment.`,
                    `Lower or return to the start position over 2–3 seconds, preserving continuous tension.`
                ];
                mistakes = [
                    "Rushing reps and utilizing uncontrolled momentum.",
                    "Losing spinal alignment and core bracing.",
                    "Restricting range of motion."
                ];
                tips = `Establish a strong mind-muscle connection with the ${muscleName}.`;
                volume = "3–4 sets of 10–12 reps.";
                break;
        }

        return { instructions, commonMistakes: mistakes, tips, volume };
    }
}

/**
 * Main accessor: retrieves full exercise guide with instructions, mistakes, tips, and volume.
 */
export function getExerciseGuide(exerciseId, lang = 'en') {
    const isUk = (lang || 'en').startsWith('uk');
    const langKey = isUk ? 'uk' : 'en';

    const baseExercise = findExercise(exerciseId, isUk);
    const catalogExercise = baseExercise || { id: exerciseId, name: exerciseId };

    const curated = CURATED_GUIDES[exerciseId] && CURATED_GUIDES[exerciseId][langKey];

    const localizedTitle = (isUk ? catalogExercise.nameUk || catalogExercise.name : catalogExercise.name)
        || (exerciseId ? exerciseId.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()) : "");

    const primaryMuscle = catalogExercise.primaryMuscle || 'chest';
    const secondaryMuscles = (catalogExercise.muscleGroups || [])
        .filter(mg => mg.priority !== 'primary')
        .map(mg => mg.group);

    const equipName = (EQUIPMENT_NAMES[langKey] && EQUIPMENT_NAMES[langKey][catalogExercise.equipment])
        || catalogExercise.equipment
        || (isUk ? "Спортивне спорядження" : "Equipment");

    // Dynamic guide fallback
    const dynamic = generateDynamicGuide(catalogExercise, langKey);

    const instructions = (catalogExercise.instructions && catalogExercise.instructions.length > 0)
        ? catalogExercise.instructions
        : (curated?.instructions || dynamic.instructions);

    const commonMistakes = (catalogExercise.commonMistakes && catalogExercise.commonMistakes.length > 0)
        ? catalogExercise.commonMistakes
        : (curated?.commonMistakes || dynamic.commonMistakes);

    const tips = curated?.tips || dynamic.tips;
    const volume = curated?.volume || dynamic.volume;

    const diagramUrl = catalogExercise.diagramUrl || null;

    return {
        id: exerciseId,
        name: localizedTitle,
        equipment: equipName,
        rawEquipment: catalogExercise.equipment,
        primaryMuscle,
        primaryMuscleName: (MUSCLE_NAMES[langKey] && MUSCLE_NAMES[langKey][primaryMuscle]) || primaryMuscle,
        secondaryMuscles,
        instructions,
        commonMistakes,
        tips,
        volume,
        diagramUrl
    };
}
